import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Link2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { nok } from "@/pages/admin/budsjett/budsjettUtils";
import ProposalEditor from "./ProposalEditor";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

export default function ProposalSection({ companyId, aktor }) {
  const [proposals, setProposals] = useState(null);
  const [products, setProducts] = useState([]);
  const [aapen, setAapen] = useState(null);
  const [oppretter, setOppretter] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/admin/os/proposals?companyId=${companyId}`);
      setProposals(data.proposals);
      setProducts(data.products);
    } catch (e) {
      feil(e);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const opprett = async () => {
    if (oppretter) return;
    setOppretter(true);
    try {
      const { data } = await api.post("/admin/os/proposals", { companyId, recommendedKey: "founding", actor: aktor });
      setProposals((prev) => [data.item, ...(prev || [])]);
      setAapen(data.item.id);
      toast.success("Proposal opprettet — juster og del lenken");
    } catch (e) {
      feil(e);
    } finally {
      setOppretter(false);
    }
  };

  const patch = async (prid, p) => {
    try {
      const { data } = await api.patch(`/admin/os/proposals/${prid}`, { ...p, actor: aktor });
      setProposals((prev) => prev.map((x) => (x.id === prid ? data.item : x)));
    } catch (e) {
      feil(e);
    }
  };

  const regenerer = async (prid) => {
    if (!window.confirm("Generere ny lenke? Den gamle slutter å virke umiddelbart.")) return;
    try {
      await api.post(`/admin/os/proposals/${prid}/regenerate`);
      toast.success("Ny lenke generert — den gamle er revokert");
      await load();
    } catch (e) {
      feil(e);
    }
  };

  const revoker = async (prid) => {
    if (!window.confirm("Revokere lenken? Mottakeren mister tilgang umiddelbart.")) return;
    try {
      await api.post(`/admin/os/proposals/${prid}/revoke`);
      toast.success("Lenken er revokert");
      await load();
    } catch (e) {
      feil(e);
    }
  };

  const slett = async (prid) => {
    if (!window.confirm("Slette proposalen permanent?")) return;
    try {
      await api.delete(`/admin/os/proposals/${prid}`);
      setAapen(null);
      setProposals((prev) => prev.filter((x) => x.id !== prid));
      toast.success("Proposalen er slettet");
    } catch (e) {
      feil(e);
    }
  };

  const kopier = (p) => {
    navigator.clipboard.writeText(`${window.location.origin}${p.sharePath}`);
    toast.success("Proposal-lenken er kopiert");
  };

  const aapenProposal = useMemo(() => (proposals || []).find((p) => p.id === aapen) || null, [proposals, aapen]);

  return (
    <div className="mt-6" data-testid="proposal-seksjon">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">
        Commercial proposal ({proposals == null ? "…" : proposals.length})
      </p>
      {(proposals || []).map((p) => (
        <div key={p.id} className="flex items-center gap-2.5 py-1.5 group" data-testid={`proposal-rad-${p.id}`}>
          <FileText size={13} className={p.active ? "text-neutral-400" : "text-red-300"} />
          <button
            type="button"
            onClick={() => setAapen(p.id)}
            className="flex-1 text-left text-[12.5px] text-neutral-800 hover:text-neutral-900 hover:underline underline-offset-2 truncate"
            data-testid={`proposal-aapne-${p.id}`}
          >
            {p.effectiveName} · {nok(p.effectivePrice)}
            <span className={`ml-2 text-[10.5px] ${p.active ? "text-emerald-600" : "text-red-500"}`}>
              {p.active ? "Aktiv" : "Revokert"}
            </span>
          </button>
          <button
            type="button"
            onClick={() => kopier(p)}
            className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-neutral-800 transition-all"
            title="Kopier lenke"
            data-testid={`proposal-kopier-${p.id}`}
          >
            <Link2 size={13} />
          </button>
        </div>
      ))}
      <button type="button" onClick={opprett} disabled={oppretter} className="adm-btn-ghost adm-btn-sm mt-1.5" data-testid="proposal-ny-btn">
        <Plus size={12} /> {oppretter ? "Oppretter …" : "Ny proposal"}
      </button>

      {aapenProposal && (
        <ProposalEditor
          proposal={aapenProposal}
          products={products}
          onPatch={(p) => patch(aapenProposal.id, p)}
          onRegenerate={() => regenerer(aapenProposal.id)}
          onRevoke={() => revoker(aapenProposal.id)}
          onDelete={() => slett(aapenProposal.id)}
          onLukk={() => setAapen(null)}
        />
      )}
    </div>
  );
}
